<template lang="pug">
  b-card.manager-accounts-form.card-white.card-no-gutters.shadow(no-body)
    wc-card-tabs(:tabs='tabs')
    b-form.form-wc.form-account-form.mt-sm-3(
      @submit.stop.prevent='formSubmit'
      @input.stop.prevent='formChange'
      @reset.stop.prevent='formReset')
      b-card-body

        wc-forms-alerts(v-if='formShowErrors' :errors='form.errors.base')

        transition(name='fade')
          fieldset(v-if='formShow' :disabled='formDisable')
            b-form-group#form-account-identity_attributes-username(
              :label="$t('activerecord.attributes.identity.username')"
              label-for='account-identity_attributes-username'
              label-cols-md='2'
              label-align-md='right')
              b-form-input#account-identity_attributes-username.form-input-first.shadow-sm.is-required(
                name='identity.username'
                type='text'
                autocomplete='username'
                :placeholder="$t('identity.placeholders.username')"
                :state='formValidationState($v.form.data.attributes.identity_attributes.username)'
                :readonly='!isAdmin'
                @focus.native='formChange'
                @input='$v.form.data.attributes.identity_attributes.username.$touch'
                v-model='identityAttributes.username'
                aria-describedby='account-identity_attributes-username-feedback'
                trim)
              wc-forms-if#account-identity_attributes-username-feedback(
                extra-class='ml-feedback'
                :attribute='$v.form.data.attributes.identity_attributes.username'
                :remote="formRemoteInvalidFeedback('identity.username')"
                :validators='{ required: {}, regExp: {}, minLength: {}, maxLength: {}, takenOrNotAllowed: {} }')

            b-form-group#form-account-email(
              :label="$t('activerecord.attributes.account.email')"
              label-for='account-email'
              label-cols-md='2'
              label-align-md='right')
              b-form-input#account-email.form-input-middle.shadow-sm.is-required(
                name='email'
                type='email'
                autocomplete='email'
                :placeholder="$t('account.placeholders.email')"
                :state='formValidationState($v.form.data.attributes.email)'
                :readonly='!isAdmin'
                @focus.native='formChange'
                v-model='$v.form.data.attributes.email.$model'
                aria-describedby='account-email-feedback'
                trim)
              wc-forms-if#account-email-feedback(
                extra-class='ml-feedback'
                :attribute='$v.form.data.attributes.email'
                :remote="formRemoteInvalidFeedback('email')"
                :validators='{ required: {}, regExp: {}, maxLength: {} }')

            b-form-group#form-account-locale(
              :label="$t('activerecord.attributes.account.locale')"
              label-for='account-locale'
              label-cols-md='2'
              label-align-md='right')
              multiselect#account-locale.form-input-middle.shadow-sm.custom-select.is-required(
                name='locale'
                v-model='accountLocaleSelected'
                :options='accountLocaleOptions'
                :class='formValidationClass($v.form.data.attributes.locale)'
                :close-on-select='true'
                :clear-on-select='false'
                :placeholder="$t('account.placeholders.locale')"
                label='label'
                track-by='value'
                :selectLabel="$t('shared.forms.multiselect.select_label')"
                :selectedLabel="$t('shared.forms.multiselect.selected_label')"
                :deselectLabel="$t('shared.forms.multiselect.deselect_label')"
                :limitText="count => $t('shared.forms.multiselect.limit_text', { count: count })"
                @input='formSelectOption')
                span(slot='noResult')
                  | {{ $t('shared.forms.multiselect.no_result') }}
                span(slot='noOptions')
                  | {{ $t('shared.forms.multiselect.no_options') }}
              wc-forms-if#account-locale-feedback(
                extra-class='ml-feedback'
                :attribute='$v.form.data.attributes.locale'
                :remote="formRemoteInvalidFeedback('locale')"
                :validators='{ required: {} }')

            b-form-group#form-account-time_zone(
              :label="$t('activerecord.attributes.account.time_zone')"
              label-for='account-time_zone'
              label-cols-md='2'
              label-align-md='right')
              multiselect#account-time_zone.form-input-last.shadow-sm.custom-select.is-required(
                name='time_zone'
                v-model='accountTimeZoneSelected'
                :options='accountTimeZoneOptions'
                :class='formValidationClass($v.form.data.attributes.time_zone)'
                :close-on-select='true'
                :clear-on-select='false'
                :placeholder="$t('account.placeholders.time_zone')"
                label='label'
                track-by='value'
                :selectLabel="$t('shared.forms.multiselect.select_label')"
                :selectedLabel="$t('shared.forms.multiselect.selected_label')"
                :deselectLabel="$t('shared.forms.multiselect.deselect_label')"
                :limitText="count => $t('shared.forms.multiselect.limit_text', { count: count })"
                @input='formSelectOption')
                span(slot='noResult')
                  | {{ $t('shared.forms.multiselect.no_result') }}
                span(slot='noOptions')
                  | {{ $t('shared.forms.multiselect.no_options') }}
              wc-forms-if#account-time_zone-feedback(
                extra-class='ml-feedback'
                :attribute='$v.form.data.attributes.time_zone'
                :remote="formRemoteInvalidFeedback('time_zone')"
                :validators='{ required: {} }')

      b-card-footer.border-0.bg-white
        wc-forms-buttons#form-account-buttons(:disabled='formDisable || $v.form.data.$invalid')

    loading(:active.sync='isLoading' :can-cancel-esc='true' :on-cancel='onCancel')
</template>

<script>
import Tabs from '@views/container/manager/accounts/shared/tabs'
import WcFormsButtons from '@components/shared/forms/WcFormsButtons'
import apiAccounts from '@services/api/manager/accounts'
import { Form, mapSelects, mapNested } from '@common/form'
import { regExp } from '@common/form/validations'
import { required, minLength, maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'manager-accounts-form',
  mixins: [Form, Tabs],
  components: {
    WcFormsButtons,
  },
  computed: {
    ...mapSelects([
      {
        name: 'accountLocale',
        default: 'attributes.locale',
        attribute: 'attributes.locale',
        collection: { data: 'available_locales.data', key: 'id', value: 'attributes.name' },
      },
      {
        name: 'accountTimeZone',
        default: 'attributes.time_zone',
        attribute: 'attributes.time_zone',
        collection: { data: 'time_zones.data', key: 'id', value: 'attributes.name' },
      },
    ]),
    ...mapNested([
      {
        name: 'identityAttributes',
        default: { data: 'relationships.identity.data', key: 'id', type: 'identity' },
        attributes: 'attributes.identity_attributes',
      },
    ]),
    isAdmin() {
      /* This $root is used because of a bug on server side. */
      return this.$root.$store.getters['auth/isAdmin']
    },
    accountUpdateQuery() {
      return this.action === 'update' ? { id: this.account_id } : {}
    },
    apiParams() {
      return {
        get: [{ id: this.account_id }, {}],
        update: [
          { id: this.account_id },
          { [this.apiModel]: this.formSubmitData(this.$getDeep(this.form, 'data.attributes')) },
        ],
        uniqueIdentityUsername: [
          {},
          {
            ...this.accountUpdateQuery,
            term: this.identityAttributes.username,
          },
          { responseErrorRaw: true },
        ],
      }
    },
  },
  methods: {
    apiCallback(callback) {
      if (callback === 'submit-success') this.$emit('account-changed')
    },
  },
  validations() {
    return {
      form: {
        data: {
          attributes: {
            identity_attributes: {
              username: {
                required,
                regExp: regExp(this.$wc.conf.regex.username_browser),
                minLength: minLength(this.$wc.conf.limits.min_username),
                maxLength: maxLength(this.$wc.conf.limits.max_username),
                takenOrNotAllowed: async () => {
                  /* Touch attribute first. */
                  this.identityAttributes.username

                  /* Validate only if. */
                  if (
                    this.validateIf(
                      'identity_attributes.username',
                      ['required', 'regExp', 'minLength', 'maxLength'],
                      { if: 'invalid' }
                    )
                  )
                    return true

                  /* Validate remote. */
                  return await new Promise((resolve) => {
                    clearTimeout(this.form.timer.uniqueIdentityUsername)
                    this.form.timer.uniqueIdentityUsername = setTimeout(resolve, 600)
                  }).then(() => {
                    if (this.apiRequest && this.apiRequest.source) this.apiRequest.source.cancel()
                    this.apiRequest = this.formRequest('uniqueIdentity', 'uniqueIdentityUsername')
                    return this.apiRequest.promise // eslint-disable-line promise/no-nesting
                      .then(() => {
                        return true
                      })
                      .catch((error) => {
                        return error.response && error.response.status === 422 ? false : true
                      })
                  })
                },
                remote: () => this.formRemoteValid('identity.username'),
              },
            },
            email: {
              required,
              regExp: regExp(this.$wc.conf.regex.email_browser),
              maxLength: maxLength(this.$wc.conf.limits.max_email),
              remote: () => this.formRemoteValid('email'),
            },
            locale: {
              required,
              remote: () => this.formRemoteValid('locale'),
            },
            time_zone: {
              required,
              remote: () => this.formRemoteValid('time_zone'),
            },
          },
        },
      },
    }
  },
  data() {
    return {
      apiBase: apiAccounts,
      apiModel: 'account',
      account_id: this.$route.params.account_id,
    }
  },
}
</script>
